import { useMemo } from 'react';

const DEFAULT_WEIGHT = 1;
const WEIGHTS = {
  dominant: 2,
  normal: 1,
  staple: 0.5
};

const toFinitePositive = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : fallback;
};

const resolveIngredientWeight = (ingredient = {}) => {
  const ingredientType = String(ingredient?.ingredientType || '').trim().toLowerCase();
  if (ingredientType === 'staple') return WEIGHTS.staple;

  const rawWeightType = String(
    ingredient?.weightType ||
    ingredient?.importance ||
    ingredient?.priority ||
    ingredient?.role ||
    ''
  ).trim().toLowerCase();

  if (rawWeightType.includes('dominant')) return WEIGHTS.dominant;
  if (rawWeightType.includes('staple')) return WEIGHTS.staple;
  if (rawWeightType.includes('ana') || rawWeightType.includes('main')) return WEIGHTS.dominant;
  return WEIGHTS.normal;
};

const getSourceIngredients = (recipe = {}) => {
  if (Array.isArray(recipe?.scaledIngredients) && recipe.scaledIngredients.length > 0) return recipe.scaledIngredients;
  if (Array.isArray(recipe?.ingredients) && recipe.ingredients.length > 0) return recipe.ingredients;
  if (Array.isArray(recipe?.malzemeler) && recipe.malzemeler.length > 0) return recipe.malzemeler;
  return [];
};

const getRemainingDays = (ingredient = {}) => {
  return toFinitePositive(
    ingredient?.remainingDays ??
    ingredient?.daysRemaining ??
    ingredient?.kalanGun ??
    ingredient?.remaining_days,
    0
  );
};

const normalizeName = (value = '') => {
  return String(value || '')
    .toLocaleLowerCase('tr-TR')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const buildMissingNameSet = (recipe = {}) => {
  const missingList = (() => {
    const candidates = [
      recipe?.match?.missingIngredients,
      recipe?.facts?.missingIngredients,
      recipe?.missingIngredients,
    ];
    for (const c of candidates) {
      if (Array.isArray(c) && c.length > 0) return c;
    }
    return [];
  })();

  const set = new Set();
  if (!Array.isArray(missingList)) return set;

  missingList.forEach((entry) => {
    const rawName = typeof entry === 'string'
      ? entry
      : (entry?.name || entry?.ingredient_name || entry?.ingredientName || entry?.malzeme || '');
    const normalized = normalizeName(rawName);
    if (normalized) set.add(normalized);
  });
  return set;
};

const resolveIngredientName = (ingredient = {}) => {
  return String(
    ingredient?.name ||
    ingredient?.ingredient_name ||
    ingredient?.ingredientName ||
    ingredient?.malzeme ||
    ingredient?.urun_adi ||
    ''
  );
};

const resolveAvailability = (ingredient = {}, missingNameSet = new Set()) => {
  if (ingredient?.isAvailable === false || ingredient?.available === false || ingredient?.inInventory === false || ingredient?.hasStock === false) {
    return false;
  }
  if (ingredient?.isAvailable === true || ingredient?.available === true || ingredient?.inInventory === true || ingredient?.hasStock === true) {
    return true;
  }

  const normalizedName = normalizeName(resolveIngredientName(ingredient));
  if (normalizedName && missingNameSet.has(normalizedName)) return false;
  return true;
};

export default function useRecipeInsightData({ recipe, userProfile } = {}) {
  return useMemo(() => {
    const safeRecipe = recipe || {};
    const ingredients = getSourceIngredients(safeRecipe);
    const missingNameSet = buildMissingNameSet(safeRecipe);

    const weighted = ingredients.reduce(
      (acc, ingredient) => {
        const weight = resolveIngredientWeight(ingredient);
        const hasIngredient = resolveAvailability(ingredient, missingNameSet);
        acc.weightedTotal += weight;
        if (hasIngredient) acc.weightedMatched += weight;
        return acc;
      },
      { weightedMatched: 0, weightedTotal: 0 }
    );

    const weightedMatchRatio = weighted.weightedTotal > 0
      ? weighted.weightedMatched / weighted.weightedTotal
      : 0;

    const rescuedIngredients = ingredients.filter((ingredient) => getRemainingDays(ingredient) > 0);
    const urgencyScore = rescuedIngredients.reduce((sum, ingredient) => {
      const remainingDays = getRemainingDays(ingredient);
      if (remainingDays <= 0) return sum;
      return sum + (1 / remainingDays);
    }, 0);

    return {
      recipe: safeRecipe,
      userProfile: userProfile || null,
      match: {
        weightedMatched: Number(weighted.weightedMatched.toFixed(2)),
        weightedTotal: Number(weighted.weightedTotal.toFixed(2)),
        weightedMatchRatio: Number(weightedMatchRatio.toFixed(4)),
        missingCountFromContract: missingNameSet.size
      },
      skt: {
        rescuedCount: rescuedIngredients.length,
        urgencyScore: Number(urgencyScore.toFixed(4))
      },
      metadata: {
        engineVersion: 'recipe_insight_v1'
      }
    };
  }, [recipe, userProfile]);
}
